import EmptyState from 'components/EmptyState';
import { Launch as LaunchIcon, LayoutGridIcon, LockIcon, PlusIcon } from 'components/icons';
import PageTitle from 'components/PageTitle';
import { Button } from 'components/ui/button';
import useAuth from 'hooks/useAuth';
import { useIsMobile } from 'hooks/useIsMobile';
import { useReserveBottomRow } from 'hooks/useReserveBottomRow';
import PaddedLayout from 'layout/MainLayout/PaddedLayout';
import { getTitle } from 'pages/dashboard/DashBoard';
import { useDashboardForm } from 'pages/dashboard/useDashboardForm';
import { useTiqTime } from 'pages/dashboard/useTiqTime';
import { memo, type ReactNode, useEffect, useId, useMemo, useRef, useState } from 'react';
import { FormProvider } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useGetHomeQuery, useSaveHomeLayoutMutation } from 'store/api/home';
import { useTourMode } from 'tour/tourMode';
import { useTour } from 'tour/TourProvider';
import WidgetsTourButton from 'tour/WidgetsTourButton';
import type { LockedHomeEntry } from 'types/homeWidgetSharing';
import { FeatureFlag } from 'types/features';
import type { HomeEntry } from 'types/homeWidgets';
import { cn } from 'utils/cn';
import { t } from 'utils/getTranslations';
import { removeExpiredInvoiceCookies } from 'utils/localStorage';
import { useHasPermissions } from 'utils/permissionUtil';
import { useIsFeatureEnabled } from 'utils/useFeatureFlags';
import ContinueSection from './ContinueSection';
import HomeSection, { HOME_LINK_CLASS } from './HomeSection';
import MyCasesSection from './MyCasesSection';
import MyTimeRecordsSection from './MyTimeRecordsSection';
import MyTimeSection from './MyTimeSection';
import SavedForLaterSection from './SavedForLaterSection';
import ToReviewSection from './ToReviewSection';
import { AddWidgetDialog } from './widgets/add/AddWidgetDialog';
import { WidgetCreator } from './widgets/creator/WidgetCreator';
import { addEntry, toLayoutSave } from './widgets/grid/layout';
import { WidgetGrid } from './widgets/grid/WidgetGrid';
import { ForbiddenState } from './widgets/kpi/KpiParts';
import { WidgetHost } from './widgets/WidgetHost';

const TITLE_CLASS = 'text-foreground text-page-title text-balance';

/** The widgets' load failure, as its empty state titles it and as the page's live region says it. */
const WIDGETS_ERROR_TITLE_ID = 'home.widgets.error.loading';
/** The states of a guided tour's step that show Home being arranged: the two dialogs open over it, as for a person. */
const TOUR_ARRANGING = ['arranging', 'add-gallery', 'create'];

const noop = () => {};

const isNotSavedForLater = (entry: HomeEntry) => entry.kind !== 'BUILT_IN' || entry.builtIn !== 'SAVED_FOR_LATER';

/**
 * The new design's Home: a grid of widgets the person arranges. The layout comes from the server and from nowhere
 * else; the dashboard's blocks are its built-in widgets. Customise opens a copy of the layout to move, resize,
 * remove and add in, which Done saves in one write and Cancel, or leaving the page, drops.
 *
 * What the firm put on this Home comes first, in a grid of its own that is never arranged, and the two parts are
 * then each under a heading. With nothing from the firm there is the one grid and no heading.
 *
 * A guided tour shows its sample Home, and opens what its running step names: Home being arranged, the Add a widget
 * dialog on the firm's gallery, the creator on a copy of the first widget. None of it is the person's: a layout they
 * were arranging stays out of sight for as long as the tour runs, and nothing the tour opens can be pressed.
 */
const Home = memo(function Home({ tourState }: { tourState?: string }) {
    const methods = useDashboardForm();
    const { user } = useAuth();
    const userId = user?.id;
    const canSeeFinalizedHours = useHasPermissions(['READ.DASHBOARD.FINALIZED_HOURS']);
    // The invoice list To review reads answers 403 without it.
    const canReadInvoices = useHasPermissions(['READ.INVOICES.MATTER_ACCESS']);
    const { isEnabled: isTiqTimeEnabled, frontendUrl: tiqTimeFrontendUrl, unPostedMinutesFormatted } = useTiqTime();
    const reserveBottomRow = useReserveBottomRow();
    const isMobile = useIsMobile();
    const { data: served, isError, refetch } = useGetHomeQuery();
    const offersDrafts = useIsFeatureEnabled(FeatureFlag.SAVE_AS_DRAFT);
    // While drafts are off, Saved for later is on no Home, whatever its layout holds. An own layout that holds it
    // keeps it when arranged, last, and the room it takes.
    const hiddenOwn = useMemo(
        () => (served && !offersDrafts ? served.own.filter((entry) => !isNotSavedForLater(entry)) : []),
        [served, offersDrafts]
    );
    const home = useMemo(
        () =>
            served && !offersDrafts
                ? {
                      ...served,
                      locked: served.locked.filter(isNotSavedForLater),
                      own: served.own.filter(isNotSavedForLater),
                      maxOwnEntries: served.maxOwnEntries - hiddenOwn.length,
                  }
                : served,
        [served, offersDrafts, hiddenOwn]
    );
    const [saveLayout, { isLoading: isSaving }] = useSaveHomeLayoutMutation();
    const touring = useTourMode();
    const actionsRef = useRef<HTMLDivElement>(null);
    // The layout while the person arranges it; null the rest of the time.
    const [ownDraft, setDraft] = useState<HomeEntry[] | null>(null);
    const [isAdding, setIsAdding] = useState(false);
    // In a tour: the sample layout, for the steps that show Home arranged, and never the person's own.
    const tourArranges = !!tourState && TOUR_ARRANGING.includes(tourState);
    const draft = touring ? (tourArranges && home?.own) || null : ownDraft;
    const adding = touring ? tourState === 'add-gallery' : isAdding;

    useEffect(() => {
        if (userId) {
            removeExpiredInvoiceCookies(userId);
        }
    }, [userId]);

    // Mounted one task late, as on the classic dashboard, so the header's breadcrumb animation paints first.
    const [showBody, setShowBody] = useState(false);
    useEffect(() => {
        const id = setTimeout(() => setShowBody(true), 0);
        return () => clearTimeout(id);
    }, []);

    const tiqTimeLink =
        isTiqTimeEnabled && tiqTimeFrontendUrl ? (
            <a
                href={tiqTimeFrontendUrl}
                target="_blank"
                rel="noopener noreferrer"
                className={cn(HOME_LINK_CLASS, 'flex w-fit items-center gap-1')}
            >
                <LaunchIcon fontSize="small" />
                {unPostedMinutesFormatted}
                <FormattedMessage id="tiqtime.suggested-by-tiqtime" />
            </a>
        ) : null;

    const entries = draft ?? home?.own ?? [];
    // The sample widget the tour's creator step opens a copy of: the first the sample person built.
    const tourWidget = home?.own.find((entry) => entry.kind === 'WIDGET')?.widget;
    const locked: LockedHomeEntry[] = home?.locked ?? [];
    const showsMyTime =
        canSeeFinalizedHours && entries.some((entry) => entry.kind === 'BUILT_IN' && entry.builtIn === 'MY_TIME');

    // A built-in closed to the viewer is its titled card naming the access it needs, its section never mounted.
    const forbiddenCard = (titleId: string, areaId: string, stateTitleId?: string) => (
        <HomeSection title={t(titleId)}>
            <ForbiddenState area={t(areaId)} titleId={stateTitleId} className="py-4" />
        </HomeSection>
    );

    const renderEntry = (entry: LockedHomeEntry) => {
        if (entry.kind === 'WIDGET') {
            return <WidgetHost widget={entry.widget} size={entry.size} lock={entry.lock} />;
        }
        switch (entry.builtIn) {
            case 'MY_CASES':
                return <MyCasesSection />;
            case 'CONTINUE':
                return <ContinueSection />;
            case 'MY_TIME':
                return canSeeFinalizedHours ? (
                    <MyTimeSection size={entry.size} actions={tiqTimeLink} />
                ) : (
                    forbiddenCard('home.my-time', 'dashboard.finalized-hours')
                );
            case 'MY_TIME_RECORDS':
                return (
                    <>
                        {/* The TiqTime link is not behind the hours permission, so without My time it sits here. */}
                        {showsMyTime ? null : tiqTimeLink}
                        <MyTimeRecordsSection />
                    </>
                );
            case 'SAVED_FOR_LATER':
                return <SavedForLaterSection />;
            case 'TO_REVIEW':
                return canReadInvoices ? (
                    <ToReviewSection />
                ) : (
                    forbiddenCard('home.to-review-or-approve', 'menu.invoices', 'home.to-review-forbidden.title')
                );
        }
    };

    const stopArranging = () => {
        setDraft(null);
        setIsAdding(false);
    };

    const saveArranged = async () => {
        try {
            // Untouched, the layout is not written: a Home still on the default keeps following it.
            if (draft && draft !== home?.own) {
                await saveLayout(toLayoutSave([...draft, ...hiddenOwn])).unwrap();
            }
            stopArranging();
        } catch {
            // The failure is reported where every request's is; what was arranged stays open.
        }
    };

    const ownGrid = (
        <WidgetGrid
            entries={entries}
            renderEntry={renderEntry}
            arranging={draft ? { onChange: setDraft, onAdd: () => setIsAdding(true) } : undefined}
        />
    );

    return (
        <FormProvider {...methods}>
            <PaddedLayout>
                {/* The last row's actions scroll clear of an occupied bottom row; without one, an ordinary gap. */}
                <div className={cn('flex flex-col gap-6', reserveBottomRow ? 'pb-24' : 'pb-6')}>
                    {draft ? (
                        <div className="flex flex-col gap-3">
                            <div className="flex flex-wrap items-center gap-2">
                                <h1 className={cn(TITLE_CLASS, 'basis-full sm:flex-1 sm:basis-0')}>
                                    {t('home.widgets.arranging.title')}
                                </h1>
                                <Button
                                    variant="outline"
                                    data-tour="home-add-widget"
                                    className="border-border-control px-3 max-sm:h-11 max-sm:flex-1"
                                    onClick={() => setIsAdding(true)}
                                >
                                    <PlusIcon />
                                    {t('home.widgets.add')}
                                </Button>
                                <Button
                                    variant="ghost"
                                    className="text-foreground-secondary px-3 max-sm:h-11 max-sm:px-3.5"
                                    onClick={stopArranging}
                                >
                                    {t('cancel')}
                                </Button>
                                <Button
                                    variant="secondary"
                                    className="px-4 font-semibold max-sm:h-11 max-sm:px-[18px]"
                                    disabled={isSaving}
                                    onClick={saveArranged}
                                >
                                    {t('done')}
                                </Button>
                            </div>
                            <p
                                role="status"
                                className="border-changed-border bg-changed text-changed-ink text-meta rounded-[10px] border px-3.5 py-2.5"
                            >
                                {t(isMobile ? 'home.widgets.arranging.hint-touch' : 'home.widgets.arranging.hint')}
                            </p>
                        </div>
                    ) : (
                        <div className="flex flex-wrap items-center gap-3">
                            <h1 tabIndex={-1} className={cn(TITLE_CLASS, 'flex-1 outline-none')}>
                                {getTitle(user?.name ?? null)}
                            </h1>
                            {home ? (
                                <div ref={actionsRef} className="flex items-center gap-3">
                                    {/* Keyed by the person: the offer is decided once for whoever is signed in. */}
                                    <WidgetsTourButton key={userId} offerAnchor={actionsRef} />
                                    <Button
                                        variant="outline"
                                        data-tour="home-customise"
                                        className="border-border-control gap-2 px-3 has-data-[icon=inline-start]:pl-2.5 max-sm:size-11 max-sm:has-data-[icon=inline-start]:pl-3"
                                        onClick={() => setDraft(home.own)}
                                    >
                                        <LayoutGridIcon data-icon="inline-start" />
                                        <span className="max-sm:sr-only">{t('home.widgets.customise')}</span>
                                    </Button>
                                </div>
                            ) : null}
                        </div>
                    )}
                    {showBody && home && locked.length > 0 ? (
                        <>
                            <HomePart
                                part="firm"
                                title={t(draft ? 'home.widgets.part.firm-locked' : 'home.widgets.part.firm')}
                            >
                                {/* Out of reach and dimmed while the person arranges what is theirs. */}
                                <div inert={!!draft} className={cn('transition-opacity', draft && 'opacity-[0.72]')}>
                                    <WidgetGrid entries={locked} renderEntry={renderEntry} />
                                </div>
                            </HomePart>
                            {/* The wider gap leaves room for the controls that sit over the first row's cells. */}
                            <HomePart
                                part="own"
                                title={t('home.widgets.part.own')}
                                className={cn(draft && 'gap-[22px]')}
                            >
                                {ownGrid}
                            </HomePart>
                        </>
                    ) : null}
                    {showBody && home && locked.length === 0 && (entries.length > 0 || draft) ? ownGrid : null}
                    {/* Nothing of the firm's and nothing of the person's: the way to the first widget. */}
                    {showBody && home && locked.length === 0 && entries.length === 0 && !draft ? (
                        <EmptyState
                            titleId="home.empty.title"
                            bodyId="home.empty.body"
                            action={
                                <Button
                                    variant="secondary"
                                    onClick={() => {
                                        setDraft(home.own);
                                        setIsAdding(true);
                                    }}
                                >
                                    <PlusIcon />
                                    {t('home.widgets.add')}
                                </Button>
                            }
                            surface="home-empty"
                        />
                    ) : null}
                    {isError && !home ? (
                        <EmptyState
                            variant="error"
                            errorTitleId={WIDGETS_ERROR_TITLE_ID}
                            onRetry={refetch}
                            surface="home"
                        />
                    ) : null}
                    {/* Mounted before its text, which a live region needs to be read. */}
                    <div role="status" className="sr-only">
                        {isError && !home ? t(WIDGETS_ERROR_TITLE_ID) : ''}
                    </div>
                </div>
            </PaddedLayout>
            {home ? (
                <AddWidgetDialog
                    open={adding}
                    onClose={() => setIsAdding(false)}
                    tab={touring ? 'gallery' : undefined}
                    entries={entries}
                    maxOwnEntries={home.maxOwnEntries}
                    onAdd={(placement, size) => setDraft((current) => current && addEntry(current, placement, size))}
                />
            ) : null}
            {/* Gone with the tour, not closed by it: a closing dialog would take input again while it fades. */}
            {touring && tourWidget ? (
                <WidgetCreator
                    open={tourState === 'create'}
                    onClose={noop}
                    tab="catalogue"
                    draft={{ name: tourWidget.name, definition: tourWidget.definition }}
                />
            ) : null}
        </FormProvider>
    );
});

/**
 * Home, given the state the guided tour's running step asks of it. The tour's context changes with every move of
 * the tour and that state with few of them, so it is read here and Home renders again only when it changes.
 */
export default function HomePage() {
    const { state } = useTour();
    // Home has no `PageHeader` to name the browser tab for it.
    return (
        <>
            <PageTitle title={t('menu.home')} />
            <Home tourState={state} />
        </>
    );
}

type PartProps = {
    /** Which of the two it is, for whatever looks for it in the page. */
    part: 'firm' | 'own';
    title: string;
    className?: string;
    children: ReactNode;
};

/** One of Home's two parts, under its heading; the firm's carries a lock. */
function HomePart({ part, title, className, children }: PartProps) {
    const id = useId();
    return (
        <section aria-labelledby={id} data-part={part} className={cn('flex flex-col gap-2.5', className)}>
            <h3 id={id} className="text-label text-muted-foreground flex items-center gap-1.5 uppercase">
                {part === 'firm' ? <LockIcon className="size-3" /> : null}
                {title}
            </h3>
            {children}
        </section>
    );
}
